import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Play, Pause, SkipBack, SkipForward, RotateCcw, Volume2, VolumeX } from "lucide-react";

const SCENES = [
  { img: "/walkthrough/img/01_scan.jpg", audio: "/walkthrough/audio/scene1.mp3", caption: "Guests scan a QR to join — no app, no download" },
  { img: "/walkthrough/img/02_booths.png", audio: "/walkthrough/audio/scene2.mp3", caption: "Explore the challenge booths you set up" },
  { img: "/walkthrough/img/03_play.png", audio: "/walkthrough/audio/scene3.mp3", caption: "Play a booth — scored instantly" },
  { img: "/walkthrough/img/04_claim.png", audio: "/walkthrough/audio/scene4.mp3", caption: "Scan the Victory QR or enter the code" },
  { img: "/walkthrough/img/05_wallet.png", audio: "/walkthrough/audio/scene5.mp3", caption: "Cash lands straight in the wallet" },
  { img: "/walkthrough/img/06_gm.png", audio: "/walkthrough/audio/scene6.mp3", caption: "Game Masters run every booth" },
  { img: "/walkthrough/img/07_dashboard.png", audio: "/walkthrough/audio/scene7.mp3", caption: "Organizers track everything live" },
  { img: "/walkthrough/img/09_people.png", audio: "/walkthrough/audio/scene_people.mp3", caption: "Pre-book guests from Excel or CSV — and export everyone anytime" },
  { img: "/walkthrough/img/08_bigscreen.png", audio: "/walkthrough/audio/scene8.mp3", caption: "The big screen crowns the winner — live" },
];

export default function Walkthrough({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [ended, setEnded] = useState(false);
  const audioRef = useRef(null);

  const go = useCallback((n) => {
    setEnded(false);
    setIdx((prev) => Math.min(Math.max(n, 0), SCENES.length - 1));
  }, []);

  // Reset when (re)opened
  useEffect(() => {
    if (open) { setIdx(0); setPlaying(true); setEnded(false); }
  }, [open]);

  // Drive audio on scene / play change
  useEffect(() => {
    if (!open) return;
    const a = audioRef.current;
    if (!a) return;
    a.src = SCENES[idx].audio;
    a.muted = muted;
    a.currentTime = 0;
    if (playing) {
      const p = a.play();
      if (p && p.catch) p.catch(() => setPlaying(false));
    }
    // fallback: if audio can't load, auto-advance after 5.5s
    let t = setTimeout(() => {
      if (a.readyState < 2 && playing) handleEnded();
    }, 5500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, open]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { const p = a.play(); if (p && p.catch) p.catch(() => {}); }
    else a.pause();
  }, [playing]);

  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted]);

  const handleEnded = useCallback(() => {
    if (idx < SCENES.length - 1) go(idx + 1);
    else { setPlaying(false); setEnded(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, go]);

  const restart = () => { setIdx(0); setEnded(false); setPlaying(true); };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 bg-neutral-950 border-none overflow-hidden" data-testid="walkthrough-modal">
        <audio ref={audioRef} onEnded={handleEnded} preload="auto" />

        {/* progress segments */}
        <div className="flex gap-1.5 px-4 pt-4 pb-2 bg-neutral-950" data-testid="walkthrough-progress">
          {SCENES.map((_, i) => (
            <div key={i} className="h-1 flex-1 rounded-full bg-white/20 overflow-hidden">
              <div className={`h-full bg-primary transition-all duration-300 ${i < idx ? "w-full" : i === idx ? "w-full animate-pulse" : "w-0"}`} />
            </div>
          ))}
        </div>

        {/* stage */}
        <div className="relative w-full bg-neutral-950 flex items-center justify-center" style={{ height: "62vh" }}>
          {SCENES.map((s, i) => (
            <img
              key={i}
              src={s.img}
              alt={s.caption}
              className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${i === idx ? "opacity-100 kenburns" : "opacity-0"}`}
              data-testid={i === idx ? "walkthrough-current-img" : undefined}
            />
          ))}

          {/* caption */}
          <div className="absolute bottom-0 inset-x-0 p-5 pt-16 bg-gradient-to-t from-black/85 to-transparent">
            <p className="text-white/60 text-xs font-mono-num mb-1">STEP {idx + 1} / {SCENES.length}</p>
            <p className="text-white font-head font-bold text-lg md:text-2xl" data-testid="walkthrough-caption">{SCENES[idx].caption}</p>
          </div>

          {ended && (
            <button onClick={restart} data-testid="walkthrough-replay" className="absolute inset-0 m-auto w-16 h-16 rounded-full bg-primary text-white grid place-items-center shadow-xl">
              <RotateCcw className="w-7 h-7" />
            </button>
          )}
        </div>

        {/* controls */}
        <div className="flex items-center justify-between px-4 py-3 bg-neutral-950">
          <div className="flex items-center gap-1">
            <button onClick={() => go(idx - 1)} disabled={idx === 0} className="w-9 h-9 grid place-items-center rounded-full text-white/80 hover:bg-white/10 disabled:opacity-30" data-testid="walkthrough-prev"><SkipBack className="w-4 h-4" /></button>
            <button onClick={() => (ended ? restart() : setPlaying((p) => !p))} className="w-11 h-11 grid place-items-center rounded-full bg-primary text-white" data-testid="walkthrough-playpause">
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button onClick={() => go(idx + 1)} disabled={idx === SCENES.length - 1} className="w-9 h-9 grid place-items-center rounded-full text-white/80 hover:bg-white/10 disabled:opacity-30" data-testid="walkthrough-next"><SkipForward className="w-4 h-4" /></button>
          </div>
          <button onClick={() => setMuted((m) => !m)} className="w-9 h-9 grid place-items-center rounded-full text-white/80 hover:bg-white/10" data-testid="walkthrough-mute">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
